import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle,
  History,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/layout/page-header';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { usePermission } from '@/features/auth/auth.store';
import { cn } from '@/lib/utils';
import { ColorField, Field, ImageField, LogsSheet, MenuTile } from './catalog-shared';
import {
  useCategories,
  useDeleteCategory,
  useDepartments,
  useSaveCategory,
  type Category,
  type CategoryInput,
} from './catalog.api';

/**
 * Categories — the second level of the storefront menu, each inside a
 * department.
 *
 * The list is unpaged and can run to a few hundred rows, so it filters by
 * department and by name here rather than making somebody scroll: the original
 * offered column sorting and nothing else.
 */

const ANY = 'ANY';

const EMPTY: CategoryInput = {
  nameSpa: '',
  nameEng: '',
  imageUrl: '',
  color: '#ffffff',
  borderColor: '#ffffff',
  ordering: 0,
  superSectionId: null,
  superSectionName: null,
  superSectionNameEng: null,
  superSectionImageUrl: null,
  aisle: '',
  isAlcohol: false,
};

export function CategoriesPage() {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.resolvedLanguage ?? 'en') === 'en';
  const isAdmin = usePermission('isSuperAdmin');

  const list = useCategories();
  const departments = useDepartments();
  const save = useSaveCategory();
  const remove = useDeleteCategory();

  const [query, setQuery] = useState('');
  const [departmentId, setDepartmentId] = useState(ANY);
  const [editing, setEditing] = useState<CategoryInput | null>(null);
  const [logsFor, setLogsFor] = useState<Category | null>(null);

  const name = (row: Category) => (isEnglish ? row.nameEng : row.nameSpa) || '—';
  const departmentName = (row: Category) =>
    (isEnglish ? row.superSectionNameEng : row.superSectionName) || '—';

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (list.data ?? [])
      .filter(
        (row) => departmentId === ANY || row.superSectionId === departmentId
      )
      .filter(
        (row) =>
          !needle ||
          `${row.nameEng ?? ''} ${row.nameSpa ?? ''} ${row.aisle ?? ''}`
            .toLowerCase()
            .includes(needle)
      )
      .sort(
        (a, b) =>
          (a.ordering ?? 0) - (b.ordering ?? 0) ||
          name(a).localeCompare(name(b))
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, query, departmentId, isEnglish]);

  const isEdit = !!editing?.id;
  const set = (patch: Partial<CategoryInput>) =>
    setEditing((current) => (current ? { ...current, ...patch } : current));

  const failed = () => toast.error(t('catalog.actionFailed'));

  const filterFields: FilterField[] = [
    {
      id: 'department',
      label: t('catalog.department'),
      value: departmentId,
      onChange: setDepartmentId,
      options: [
        { value: ANY, label: t('catalog.allDepartments') },
        ...(departments.data ?? []).map((department) => ({
          value: department.id,
          label: (isEnglish ? department.nameEng : department.name) || '—',
        })),
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('catalog.categoriesTitle')}
        description={t('catalog.categoriesSubtitle')}
        actions={
          <>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t('catalog.reload')}
              disabled={list.isFetching}
              onClick={() => void list.refetch()}
            >
              <RefreshCw className={list.isFetching ? 'animate-spin' : ''} />
            </Button>
            <Button size="sm" onClick={() => setEditing(EMPTY)}>
              <Plus />
              <span className="hidden sm:inline">{t('catalog.createNew')}</span>
            </Button>
          </>
        }
      />

      <FilterBar
        busy={list.isFetching}
        search={
          <div className="relative">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('catalog.searchCategories')}
              aria-label={t('catalog.searchCategories')}
              className="h-10 pl-9"
            />
          </div>
        }
        fields={filterFields}
        summary={
          list.isSuccess ? (
            <p className="text-muted-foreground text-sm">
              {t('catalog.showingOf', {
                shown: rows.length,
                total: list.data?.length ?? 0,
              })}
            </p>
          ) : null
        }
      />

      {list.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('catalog.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void list.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {list.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <Skeleton className="size-12 shrink-0 rounded-lg" />
              <Skeleton className="h-4 w-52" />
            </div>
          ))}
        </div>
      )}

      {list.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={list.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {t('catalog.noCategories')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={list.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              list.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((row) => (
              <div
                key={row.id}
                className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center"
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <MenuTile
                    size="sm"
                    imageUrl={row.imageUrl}
                    color={row.color}
                    borderColor={row.borderColor}
                  />
                  <div className="min-w-0">
                    <p className="truncate font-medium">{name(row)}</p>
                    <p className="text-muted-foreground truncate text-xs">
                      {isEnglish ? row.nameSpa : row.nameEng}
                    </p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                      <Badge variant="secondary">{departmentName(row)}</Badge>
                      {row.aisle && (
                        <span className="text-muted-foreground text-xs">
                          {t('catalog.aisle')} {row.aisle}
                        </span>
                      )}
                      {row.isAlcohol && (
                        <Badge variant="warning">{t('catalog.alcohol')}</Badge>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex shrink-0 flex-wrap gap-2">
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setLogsFor(row)}
                    >
                      <History className="size-3.5" />
                      {t('catalog.logs')}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditing(row)}
                  >
                    <Pencil className="size-3.5" />
                    {t('catalog.edit')}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon-sm"
                        className="text-destructive"
                        aria-label={t('catalog.delete')}
                        disabled={remove.isPending}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('catalog.delete')}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('catalog.deleteCategoryPrompt', { name: name(row) })}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={() =>
                            remove.mutate(row.id, {
                              onSuccess: () => toast.success(t('catalog.deleted')),
                              onError: failed,
                            })
                          }
                        >
                          {t('common.yes')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------- editor */}
      <Sheet
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      >
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-md"
        >
          <SheetHeader className="border-b pr-12">
            <SheetTitle>
              {isEdit ? t('catalog.editCategory') : t('catalog.newCategory')}
            </SheetTitle>
          </SheetHeader>

          {editing && (
            <div className="space-y-4 p-4">
              <div className="flex justify-center rounded-xl border p-4">
                <MenuTile
                  imageUrl={editing.imageUrl}
                  color={editing.color}
                  borderColor={editing.borderColor}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={`${t('catalog.name')} (EN)`}>
                  <Input
                    value={editing.nameEng ?? ''}
                    onChange={(e) => set({ nameEng: e.target.value })}
                  />
                </Field>
                <Field label={`${t('catalog.name')} (ES)`}>
                  <Input
                    value={editing.nameSpa ?? ''}
                    onChange={(e) => set({ nameSpa: e.target.value })}
                  />
                </Field>
              </div>

              <Field label={t('catalog.department')}>
                {/* The saved department's name comes from this list, so the
                    trigger would read blank until it arrives. */}
                {departments.isPending ? (
                  <Skeleton className="h-9 w-full" />
                ) : (
                  <Select
                    value={editing.superSectionId ?? ''}
                    onValueChange={(value) => set({ superSectionId: value })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={t('catalog.selectDepartment')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {(departments.data ?? []).map((department) => (
                        <SelectItem key={department.id} value={department.id}>
                          {(isEnglish ? department.nameEng : department.name) ||
                            '—'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('catalog.aisle')}>
                  <Input
                    value={editing.aisle ?? ''}
                    onChange={(e) => set({ aisle: e.target.value })}
                  />
                </Field>
                <Field
                  label={t('catalog.ordering')}
                  hint={t('catalog.orderingHint')}
                >
                  <Input
                    type="number"
                    value={editing.ordering ?? 0}
                    onChange={(e) => set({ ordering: Number(e.target.value) })}
                  />
                </Field>
              </div>

              <div className="flex items-center justify-between gap-4 py-1">
                <Field label={t('catalog.isAlcohol')}>
                  <p className="text-muted-foreground text-xs">
                    {t('catalog.isAlcoholHint')}
                  </p>
                </Field>
                <Switch
                  checked={editing.isAlcohol}
                  onCheckedChange={(isAlcohol) => set({ isAlcohol })}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <ColorField
                  label={t('catalog.color')}
                  value={editing.color}
                  onChange={(color) => set({ color })}
                />
                <ColorField
                  label={t('catalog.borderColor')}
                  value={editing.borderColor}
                  onChange={(borderColor) => set({ borderColor })}
                />
              </div>

              <ImageField
                label={t('catalog.image')}
                hint={t('catalog.imageRequirements')}
                value={editing.imageUrl}
                onChange={(imageUrl) => set({ imageUrl })}
              />

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  disabled={save.isPending}
                  onClick={() => setEditing(null)}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  disabled={
                    save.isPending ||
                    !editing.superSectionId ||
                    (!editing.nameEng?.trim() && !editing.nameSpa?.trim())
                  }
                  onClick={() =>
                    save.mutate(editing, {
                      onSuccess: () => {
                        toast.success(t('catalog.saved'));
                        setEditing(null);
                      },
                      onError: failed,
                    })
                  }
                >
                  {save.isPending && <Loader2 className="animate-spin" />}
                  {isEdit ? t('common.save') : t('catalog.create')}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <LogsSheet
        categoryId={logsFor?.id}
        title={logsFor ? name(logsFor) : t('catalog.logs')}
        open={logsFor !== null}
        onOpenChange={(next) => {
          if (!next) setLogsFor(null);
        }}
      />
    </div>
  );
}
